import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/components/ProtectedRoute";
import { LOGO_URL } from "@/data/content";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090B] px-5 grain relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(212,175,55,0.08),transparent_60%)]" />
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <Logo className="h-28 w-auto mx-auto mb-5" />
          <h1 className="font-display text-2xl gold-text font-700">Team Dashboard</h1>
          <p className="text-zinc-400 text-sm mt-1">South Icon Awards 2026 · Event Console</p>
        </div>

        <form
          onSubmit={submit}
          className="card-lux rounded-2xl p-8 space-y-5"
          data-testid="admin-login-form"
        >
          <div>
            <Label className="text-zinc-300">Email</Label>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 bg-[#09090B] border-amber-500/20"
              placeholder="team@email.com"
              data-testid="admin-username-input"
            />
          </div>
          <div>
            <Label className="text-zinc-300">Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 bg-[#09090B] border-amber-500/20"
              placeholder="••••••••"
              data-testid="admin-password-input"
            />
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="admin-login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full gold-gradient-bg text-black font-700 py-3 rounded-full flex items-center justify-center gap-2 hover:brightness-110 transition-all disabled:opacity-60"
            data-testid="admin-login-submit-button"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ShieldCheck className="w-5 h-5" /> Sign In</>}
          </button>
        </form>

        <button
          onClick={() => navigate("/")}
          className="mt-6 mx-auto block text-sm text-zinc-500 hover:text-amber-300"
          data-testid="admin-back-home"
        >
          ← Back to website
        </button>
      </div>
    </div>
  );
}
